// @hssna/family-web 边缘节点同步状态 hook (T4-08)
// 规格: SPEC §5 / 战役 4 T4-08 / HA 自治设计
// 后端: edge-sync 服务
//   - SSE /v1/edge/sync/status (事件: edge.offline / edge.online / sync.failed / sync.recovered)
//
// 职责:
//   - 使用 SseManager 连接 edge-sync SSE
//   - 解析事件类型, 维护 status: online / offline / degraded
//   - 暴露最近事件列表 (供横幅组件展示)
//   - 组件卸载时自动断开 SSE
//
// 边缘自治说明 (HA-DESIGN):
//   边缘节点离线时, 边缘 AI + 边缘网关进入 24h 自治模式,
//   本地告警链路仍可工作, 仅云端同步暂停。

import { useEffect, useState } from 'react';
import { SseManager } from '@hssna/ts-shared';
import type { SseEvent } from '@hssna/ts-shared';
import { getAccessTokenSync } from '../lib/api';

/** 边缘同步状态 */
export type EdgeSyncStatus = 'online' | 'offline' | 'degraded';

/** 边缘同步事件类型 */
export type EdgeSyncEventType =
  | 'edge.offline'
  | 'edge.online'
  | 'sync.failed'
  | 'sync.recovered'
  | 'message'
  | string;

/** 边缘同步事件 (前端模型) */
export interface EdgeSyncEvent {
  /** 事件 ID (来自 SSE) */
  id?: string;
  /** 事件类型 */
  type: EdgeSyncEventType;
  /** 事件数据 (已解析 JSON, 解析失败保留原始字符串) */
  data: unknown;
  /** 接收时间 (前端时间戳, ISO 8601) */
  receivedAt: string;
}

/** useEdgeSyncStatus 返回值 */
export interface UseEdgeSyncStatusResult {
  /** 当前状态 */
  status: EdgeSyncStatus;
  /** 最近一次事件 */
  lastEvent: EdgeSyncEvent | null;
  /** 事件历史 (按时间倒序, 最新优先, 最多保留 50 条) */
  events: EdgeSyncEvent[];
  /** 是否已连接 SSE */
  isConnected: boolean;
}

/** edge-sync SSE 端点 (相对路径, 由 nginx 反代到 edge-sync 服务) */
const EDGE_SYNC_SSE_URL = '/v1/edge/sync/status';

/** 命名事件类型 (除默认 message 外) */
const NAMED_EVENT_TYPES: readonly EdgeSyncEventType[] = [
  'edge.offline',
  'edge.online',
  'sync.failed',
  'sync.recovered',
];

/** 最大保留事件数 */
const MAX_EVENTS = 50;

/** 根据 SSE 事件类型推断 status */
function deriveStatus(eventType: EdgeSyncEventType): EdgeSyncStatus {
  switch (eventType) {
    case 'edge.online':
    case 'sync.recovered':
      return 'online';
    case 'edge.offline':
      return 'offline';
    case 'sync.failed':
      return 'degraded';
    default:
      return 'online';
  }
}

/** 解析 SSE 事件 data 字段 (尝试 JSON.parse, 失败则保留原始字符串) */
function parseData(raw: string): unknown {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

/**
 * 边缘节点同步状态 hook
 *
 * - 自动连接 SSE, 组件卸载时断开
 * - 维护最近 50 条事件历史
 * - 通过 status 字段暴露当前聚合状态
 *
 * @example
 * const { status, lastEvent } = useEdgeSyncStatus();
 * if (status === 'offline') {
 *   // 显示红色横幅: 边缘节点离线, 本地告警仍可工作 (24h 自治)
 * }
 */
export function useEdgeSyncStatus(): UseEdgeSyncStatusResult {
  const [status, setStatus] = useState<EdgeSyncStatus>('online');
  const [lastEvent, setLastEvent] = useState<EdgeSyncEvent | null>(null);
  const [events, setEvents] = useState<EdgeSyncEvent[]>([]);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // 云端家属端禁用 edge-sync SSE:
    //   edge-sync 是边缘端服务, 云端 nginx 未暴露 /v1/edge/* 路由,
    //   且 SseManager.buildUrl 会把 JWT token 作为 path segment 追加到 URL,
    //   导致 404 噪音. 仅在显式启用时连接 SSE.
    //   elder-screen (边缘端) 通过 VITE_ENABLE_EDGE_SYNC_SSE=true 启用.
    const enabled = import.meta.env.VITE_ENABLE_EDGE_SYNC_SSE === 'true';
    if (!enabled) {
      setStatus('online');
      setIsConnected(false);
      return;
    }

    // 服务端/SSR 安全: 仅在浏览器环境连接 SSE
    if (typeof window === 'undefined' || typeof EventSource === 'undefined') {
      return;
    }

    const manager = new SseManager({
      url: EDGE_SYNC_SSE_URL,
      getToken: () => getAccessTokenSync(),
      eventTypes: NAMED_EVENT_TYPES,
      onEvent: (event: SseEvent) => {
        const parsed: EdgeSyncEvent = {
          id: event.id,
          type: event.type,
          data: parseData(event.data),
          receivedAt: new Date().toISOString(),
        };
        setLastEvent(parsed);
        setEvents((prev) => [parsed, ...prev].slice(0, MAX_EVENTS));
        setStatus(deriveStatus(event.type));
      },
      onError: () => {
        setIsConnected(false);
        // SSE 连接错误时不直接置为 offline (避免误报),
        // 由具体事件 edge.offline 来判定
      },
    });

    manager.connect();
    setIsConnected(true);

    return () => {
      manager.disconnect();
      setIsConnected(false);
    };
  }, []);

  return {
    status,
    lastEvent,
    events,
    isConnected,
  };
}
